<script>
  import { onMount } from "svelte";
  import {
    checkBalance,
    addGiftCardToAccount,
    getAccountBalance,
  } from "../models/giftCard.js";
  import { formatPrice, formatInt } from "../services/utilities.js";
  import Alert from "../components/Alert.svelte";

  export let refer = null;
  export let callback = null;

  // binding for checkout page
  export let user = null;
  export let balance = null;
  export let giftCardId = null;
  export let checkout = false;
  export let checkoutTotal;
  export let valid = false;
  export let applyAccountBalanceAmount = null;
  export let isUserApplyingBalance = false;

  let hasAccountBalance = false;
  let accountBalance = 0;
  let gcBalance = 0;

  let error = null;
  let message = null;

  onMount(async () => {
    if (refer === "checkout" && user) {
      // if checkout, check to see if the user has an account balance
      await setAccountBalance();
    }
  });

  function isGiftCardEmpty(giftCardId) {
    if (giftCardId === "" || giftCardId === null) {
      return true;
    }
    return false;
  }

  async function applyBalanceToAccount() {
    error = null;
    message = null;
    if (isGiftCardEmpty(giftCardId)) {
      error = "Please input your gift card code.";
    } else {
      let res = await addGiftCardToAccount(giftCardId);

      if (res.error) {
        error = res.error;
      } else {
        message = `Applied ${formatPrice(res.amount)} to your account`;
        if (callback === null) return;
        callback();
      }
    }
  }

  async function setAccountBalance() {
    let accountBalanceResp = await getAccountBalance(false);

    if (accountBalanceResp.error) {
      error = accountBalanceResp.error;
      return;
    }

    if (accountBalanceResp.balance > 0) {
      hasAccountBalance = true;
    }

    // user has already applied the account balance to checkout, persist
    if (applyAccountBalanceAmount === 0) {
      accountBalance = accountBalanceResp.balance;
    } else {
      let tmpApplyAccountBalanceAmount = applyAccountBalanceAmount;
      let newAccountBalance =
        accountBalanceResp.balance - tmpApplyAccountBalanceAmount;
      accountBalance = newAccountBalance;
    }
  }

  async function removeBalanceForCheckout() {
    applyAccountBalanceAmount = 0;
    await setAccountBalance();
    isUserApplyingBalance = false;
  }

  async function useBalanceForCheckout() {
    // reset error
    error = null;

    let total = checkoutTotal();

    let usedBalance = 0;

    if (total >= accountBalance) {
      usedBalance = accountBalance;
    } else if (total < accountBalance) {
      usedBalance = total;
    }
    applyAccountBalanceAmount = usedBalance;
    isUserApplyingBalance = true;
  }

  async function setBalance() {
    error = null;
    if (isGiftCardEmpty(giftCardId)) {
      error = "Please input your gift card code.";
    } else {
      let gc = await checkBalance(giftCardId);
      if (gc.error) {
        valid = false;
        gcBalance = 0;
        error = "Gift Card not found";
      } else if (gc.remaining_balance !== null) {
        if (gc.remaining_balance === 0) {
          valid = false;
          error = "Gift card has no balance left";
        } else {
          gcBalance = gc.remaining_balance;
          valid = true;
        }
      }

      if (checkout) {
        let total = checkoutTotal();
        if (gcBalance >= total) {
          balance = total;
          gcBalance = gcBalance - total;
        } else if (gcBalance < total) {
          balance = gcBalance;
          gcBalance = 0;
        }
      } else {
        balance = gcBalance;
      }
    }
  }
</script>

<form>
  {#if refer === "my-account"}
    <span>Enter a gift card code to apply the balance to your account.</span>
    <div class="lookup form-input-group">
      <input
        id="gift-card-code"
        class="form-input"
        bind:value={giftCardId}
        required={false}
      />
      <button
        class="btn"
        on:click|preventDefault={() => applyBalanceToAccount()}
      >
        Apply
      </button>
    </div>
  {:else if refer === "checkout" && hasAccountBalance}
    <div class="form-input-group">
      {#if applyAccountBalanceAmount > 0}
        <button
          class="btn btn-outline"
          on:click|preventDefault={() => removeBalanceForCheckout()}
        >
          Don't use my gift balance
        </button>
      {:else}
        <button
          class="btn btn-lg"
          on:click|preventDefault={() => useBalanceForCheckout()}
        >
          Apply my gift balance to this order
        </button>
      {/if}
    </div>
    {#if applyAccountBalanceAmount > 0}
      <p class="balance-applied">
        <svg class="icon"><use href="/icons.svg#check" /></svg>
        {formatPrice(applyAccountBalanceAmount)} of your gift balance will be
        used on this order.
      </p>
    {:else}
      <p class="success-message">
        <i>Available gift balance: {formatPrice(accountBalance)}</i>
      </p>
    {/if}
    <p>
      <i>
        Add gift cards to your account balance
        <a href="/account/gift-cards">here</a>.
      </i>
    </p>
  {:else}
    {#if refer === "checkout"}
      <label for="gift-card-code">Gift Card Code</label>
    {/if}
    <div class="lookup form-input-group">
      <input
        id="gift-card-code"
        class="form-input"
        placeholder="Gift Card Code"
        bind:value={giftCardId}
        required={!checkout}
      />
      {#if refer === "checkout"}
        <button class="btn" on:click|preventDefault={() => setBalance()}>
          Apply
        </button>
      {:else}
        <button class="btn" on:click|preventDefault={() => setBalance()}>
          Check Balance
        </button>
      {/if}
    </div>
  {/if}
</form>

{#if message}
  <span class="success-message">
    <i>{message}</i>
  </span>
{/if}

{#if balance && !checkout}
  <div>
    <h4>Gift Card Balance: {formatPrice(balance)}</h4>
  </div>
{:else if balance && checkout}
  <div class="gift-card">
    <h3 class="text-success">
      <svg class="icon">
        <use href="/icons.svg#check" />
      </svg>
      Gift Card Applied!
    </h3>

    <p class="amount">{formatPrice(balance)}</p>
    <p>Balance Remaining: {formatPrice(gcBalance)}</p>

    <button
      class="btn btn-danger"
      title="Remove"
      on:click={() => {
        balance = 0;
        giftCardId = null;
        valid = false;
      }}
    >
      <svg class="icon">
        <use href="/icons.svg#clear" />
      </svg>
      Remove
    </button>
  </div>
{/if}

{#if error}
  <Alert type="danger" icon="warning">{error}</Alert>
{/if}

<style type="text/less">
  .lookup {
    display: flex;
    flex-direction: row;
    margin: var(--spacing-sm) 0 0;
  }
  .gift-card {
    position: relative;
    background: var(--col-gray-100);
    border-color: var(--col-success);
    margin: var(--spacing-sm) 0 0;
    border-radius: var(--border-radius);

    .amount {
      font-size: 2rem;
      font-weight: bold;
    }
  }

  .success-message {
    color: green;
  }

  .balance-applied {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--col-success-d);
  }
</style>
